/**
 * Per-event "hidden from the horizontal timeline view" preference.
 *
 * The authoritative store is the backend (`hiddenFromTimeline` on the timeline
 * event). Until that backend field exists, this localStorage layer is the
 * working fallback so the toggle functions immediately and survives reloads on
 * the same browser. Read precedence (handled by the caller): explicit server
 * boolean when present, otherwise this local override, otherwise visible.
 * ``null`` is treated as unset (backend field not yet supported).
 *
 * Storage shape: one key per timeline holding an array of hidden event ids.
 *   `tp:hiddenFromTimeline:<timelineId>` -> ["<eventId>", ...]
 */

const KEY_PREFIX = 'tp:hiddenFromTimeline:';

const storageKey = (timelineId: string): string => `${KEY_PREFIX}${timelineId}`;

const isBrowser = (): boolean =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

/** Returns the set of event ids locally marked as hidden for a timeline. */
export function getLocallyHiddenTimelineEventIds(
  timelineId: string | null | undefined,
): Set<string> {
  if (!timelineId || !isBrowser()) return new Set();
  try {
    const raw = window.localStorage.getItem(storageKey(timelineId));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return new Set(parsed.filter((id): id is string => typeof id === 'string'));
    }
  } catch {
    // Corrupt/unavailable storage — treat as no overrides.
  }
  return new Set();
}

/** True when the API returned an explicit boolean visibility value. */
export function hasDefiniteHiddenFromTimeline(value: unknown): value is boolean {
  return typeof value === 'boolean';
}

/**
 * Resolve horizontal-timeline hide state: explicit server boolean, else
 * localStorage override for the event id, else optional fallback (e.g. mergeWith).
 */
export function resolveHideFromTimeline(options: {
  serverValue: unknown;
  eventId?: string | null;
  locallyHidden?: Set<string>;
  fallback?: boolean;
}): boolean {
  if (hasDefiniteHiddenFromTimeline(options.serverValue)) {
    return options.serverValue;
  }
  if (options.eventId && options.locallyHidden?.has(options.eventId)) {
    return true;
  }
  return options.fallback === true;
}

/**
 * Curation (per-event hide/show for the horizontal timeline) requires both the
 * horizontal timeline view itself and the curation flag.
 */
export function isTimelineCurationEnabled(
  featureFlags: { [key: string]: unknown } | null | undefined,
): boolean {
  return featureFlags?.horizontalTimeline === true
    && featureFlags?.horizontalTimelineCuration === true;
}

/** Persists the hidden/shown state for a single event in localStorage. */
export function setLocalTimelineEventHidden(
  timelineId: string | null | undefined,
  eventId: string | null | undefined,
  hidden: boolean,
): void {
  if (!timelineId || !eventId || !isBrowser()) return;
  try {
    const hiddenIds = getLocallyHiddenTimelineEventIds(timelineId);
    if (hidden) {
      hiddenIds.add(eventId);
    } else {
      hiddenIds.delete(eventId);
    }
    const key = storageKey(timelineId);
    if (hiddenIds.size === 0) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, JSON.stringify(Array.from(hiddenIds)));
    }
  } catch {
    // Storage unavailable (e.g. private mode quota) — silently ignore.
  }
}
